import { IPendingEverifyRepository } from '../../application/ports/outbound/IPendingEverifyRepository';
import { PendingEverifyResult } from '../../application/domain/entities/PendingEverifyResult';
import { PendingEverifyFilter } from '../../application/domain/entities/PendingEverifyFilter';
import { SortOrder } from "@/shared/application/domain/enums/SortOrder";
import { HttpClient } from '@/shared/infra/http/HttpClient';
import { PendingEverifyRequestDto } from './dto/req/PendingEverifyRequestDto';
import { PendingEverifyResponseDto } from './dto/res/PendingEverifyResponseDto';
import { mapPendingEverifyItemDtoToEntity } from './mappers/pendingEverifyItemMapper';
import { PendingEverifyItemDto } from './dto/PendingEverifyItemDto';
import { PendingEverifyItem } from '../../application/domain/entities/PendingEverifyItem';
import { DelayReasonRequest } from '../../application/domain/entities/DelayReasonRequest';

export class ApiPendingEverifyRepository implements IPendingEverifyRepository {
  constructor(private readonly httpClient: HttpClient) { }

  async fetchPendingEverifyRecords(
    page: number,
    pageSize: number,
    columnName?: string,
    sortOrder?: SortOrder,
    filters?: PendingEverifyFilter
  ): Promise<PendingEverifyResult> {

    try {
      const request: PendingEverifyRequestDto = {
        page,
        pageSize,
        columnName,
        sortOrder,
        firstName: filters?.firstName,
        lastName: filters?.lastName,
        caseStatus: filters?.caseStatus,
        isUserActionRequired: filters?.isUserActionRequired,
        actionRequiredToResolveDuplicateCase: filters?.actionRequiredToResolveDuplicateCase,
        caseNumber: filters?.caseNumber,
        i9FormId: filters?.i9FormId,
      };

      const queryParams = new URLSearchParams({
        page: request.page.toString(),
        pageSize: request.pageSize.toString(),
      });
      if (request.columnName) {
        queryParams.append('columnName', request.columnName);
      }
      if (request.sortOrder) {
        queryParams.append('sortOrder', request.sortOrder);
      }
      if (request.firstName) {
        queryParams.append('firstName', request.firstName);
      }
      if (request.lastName) {
        queryParams.append('lastName', request.lastName);
      }
      if (request.caseStatus) {
        queryParams.append('caseStatus', request.caseStatus);
      }
      if (request.isUserActionRequired !== undefined) {
        queryParams.append('isPendingCase', String(request.isUserActionRequired));
      }
      if (request.actionRequiredToResolveDuplicateCase !== undefined) {
        queryParams.append('actionRequiredToResolveDuplicateCase', String(request.actionRequiredToResolveDuplicateCase));
      }
      if (request.caseNumber) {
        queryParams.append('caseNumber', request.caseNumber);
      }
      if (request.i9FormId) {
        queryParams.append('i9FormId', request.i9FormId);
      }

      const url = `/e-verify/case-summary?${queryParams.toString()}`;
      const response = await this.httpClient.getPaginated<PendingEverifyResponseDto>(url);

      const pagination = response.pagination || {};
      return {
        items: Array.isArray(response.data)
          ? response.data.map(mapPendingEverifyItemDtoToEntity)
          : [],
        totalPages: pagination.totalPages ?? 1,
        totalItems: pagination.totalItems ?? 0,
        currentPage: pagination.currentPage ?? 1,
      };
    } catch (error) {
      console.error('Error fetching pending e-verify items:', error);
      throw error;
    }
  }

  async fetchSingleEverifyItemByQueueId(
    everifyCaseQueueId: string
  ): Promise<PendingEverifyItem> {
    try {
      const url = `/e-verify/case-summary/${everifyCaseQueueId}`;
      const response = await this.httpClient.get<PendingEverifyItemDto>(url);

      return mapPendingEverifyItemDtoToEntity(response.data);
    } catch (error) {
      console.error('Error fetching pending e-verify items:', error);
      throw error;
    }
  }

  async recordDelayReason(everifyCaseQueueId: string, request: DelayReasonRequest): Promise<void> {
    // The API files the case in the same request, so a success means E-Verify was tried again.
    await this.httpClient.post(`/e-verify/case-summary/${everifyCaseQueueId}/delay-reason`, {
      reasonOfDelay: request.reasonOfDelay,
      ...(request.otherReasonForDelay ? { otherReasonForDelay: request.otherReasonForDelay } : {}),
    });
  }
}

